// external libraries
import { useMemo } from 'react';

// components
import { useDateNavigationStore } from 'components/shared/DateNavigation/DateNavigation.stores';
import { useDateNavigationHoursStore } from 'components/shared/DateNavigation/DateNavigationHours.stores';

// types
import type { Interval } from 'components/types';

// utils
import { generateDateRange, toNativeDate } from 'utils/date';

/**
 * The day columns both roster grids render, as `yyyy-MM-dd` strings.
 *
 * A custom period is the one case where the columns do not follow the queried interval: the picker
 * writes a range of its own, and the grid shows that range even though the queries keep fetching a
 * single day (pitfall 2 in `plans/2481-rosterplan-structuur/context.md`).
 */
const useDatesToDisplay = ({ startDate, endDate }: Interval) => {
  const period = useDateNavigationStore((state) => state.period);
  const selectedDateRange = useDateNavigationHoursStore(
    (state) => state.selectedDateRange
  );

  /**
   * Explicit `useMemo` and no React Compiler directive, against the wider repo's habit: this hook's
   * return identity is load bearing (it feeds the column memo), and the folder keeps an explicit
   * guarantee there rather than the compiler's best effort. Reasoning in README.md, "Decisions".
   */
  return useMemo(() => {
    if (period === 'custom' && selectedDateRange) {
      return generateDateRange(
        toNativeDate(selectedDateRange[0]),
        toNativeDate(selectedDateRange[1])
      );
    }

    return generateDateRange(startDate, endDate);
  }, [endDate, period, selectedDateRange, startDate]);
};

export default useDatesToDisplay;
